/**
 * PATH: packages/email-hub/src/feature/personal/AddToBoardDialog.tsx
 * FILE_ID: EMAILHUB-PERSONAL-ADDTOBOARD-001
 * PURPOSE: The cross-org board picker. Lists every board the operator can see
 *          (grouped by org), and on pick POSTs a private PersonalEmailPin so
 *          the email is cross-tagged onto that board inside the operator's
 *          Personal mirror only.
 * WHY:     FEAT-2026-06-22-CROSS-ORG-PERSONAL — "an advanced option to tag to
 *          another organisation's Kanban board". The pin is a PRIVATE pointer:
 *          it writes NO Card / Allocation into the target org, so there is no
 *          cross-org bleed for that org's other members.
 * FLOW:
 *   email → pick board (grouped by org) → useCreatePin → close + onPinned
 * DEPENDENCIES:
 *   - usePersonalData: usePersonalBoards (picker source) + useCreatePin (write).
 *   - PersonalSourcePill.orgHue: consistent org colour in the group headers.
 *   - @quotech/ui cn.
 */
import { useMemo, useState } from 'react'
import { Building2, Check, ExternalLink, Loader2, Search, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useApiAuth } from '../../_internal/components/ApiAuthGate'
import { useToast } from '../../_internal/components/Toast'
import { cn } from '../../_internal/lib/utils'
import { openEmailInOrg } from '../cross-org-actions/openEmailInOrg'
import { orgHue } from './orgColors'
import {
  useCreatePin,
  usePersonalBoards,
  type PersonalEmail,
} from './usePersonalData'

interface AddToBoardDialogProps {
  email: PersonalEmail
  onClose: () => void
  onPinned?: () => void
}

export function AddToBoardDialog({
  email,
  onClose,
  onPinned,
}: AddToBoardDialogProps) {
  const { data: groups, isLoading } = usePersonalBoards()
  const createPin = useCreatePin()
  const [query, setQuery] = useState('')
  const [pinnedBoardId, setPinnedBoardId] = useState<string | null>(null)
  const navigate = useNavigate()
  const { session, switchOrganisation } = useApiAuth()
  const { toast } = useToast()

  // Filter by free text across org + board name; drop archived boards from
  // the picker (you can't sensibly file onto an archived board).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (groups ?? [])
      .map((g) => ({
        ...g,
        boards: g.boards.filter(
          (b) =>
            !b.archived &&
            (!q ||
              b.name.toLowerCase().includes(q) ||
              g.org_name.toLowerCase().includes(q)),
        ),
      }))
      .filter((g) => g.boards.length > 0)
  }, [groups, query])

  const handlePick = async (boardId: string) => {
    if (createPin.isPending) return
    try {
      await createPin.mutateAsync({
        email_id: email.id,
        target_board_id: boardId,
      })
      setPinnedBoardId(boardId)
      onPinned?.()
      // Brief success beat so the tick is visible, then dismiss.
      window.setTimeout(onClose, 650)
    } catch {
      /* mutation surfaces its own error; leave the dialog open to retry */
    }
  }

  const openCopyForDirectAllocation = async (
    copy: PersonalEmail['present_in_orgs'][number],
  ) => {
    try {
      const outcome = await openEmailInOrg({
        emailId: copy.email_id,
        targetOrgId: copy.org_id,
        targetOrgName: copy.org_name,
        currentOrgId: session?.organisation?.id ?? null,
        switchOrganisation,
        navigate,
      })
      if (outcome === 'switch-unavailable') {
        toast('Open this email from the organisation switcher first.', 'info')
        return
      }
      onClose()
    } catch {
      toast(`Could not switch to ${copy.org_name}.`, 'error')
    }
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center bg-black/40 p-4 pt-[10vh]"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-[70vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900">
              Add to a board
            </h2>
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {email.subject || '(no subject)'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="border-b border-slate-100 px-4 py-2">
          <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search boards or organisations…"
              className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
            />
          </div>
          <p className="mt-1.5 text-[11px] leading-snug text-slate-400">
            Pinning files this email onto the board in your private Personal
            workspace. It does not move the email or notify the other org.
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading boards…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">
              No boards match.
            </div>
          ) : (
            filtered.map((group) => {
              const visibleCopy = email.present_in_orgs.find(
                (copy) => copy.org_id === group.org_id,
              )
              return (
                <section key={group.org_id} className="mb-2">
                  <div className="mx-2 mb-1 flex items-center justify-between gap-2">
                    <div
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
                        orgHue(group.org_id),
                      )}
                    >
                      <Building2 className="h-3 w-3" />
                      {group.org_name}
                      {group.org_id === email.source_org_id && (
                        <span className="opacity-70">· source</span>
                      )}
                    </div>
                    {visibleCopy && (
                      <button
                        type="button"
                        onClick={() => void openCopyForDirectAllocation(visibleCopy)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline"
                        title={`Switch to ${group.org_name}, open its copy, then allocate there`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        Open copy to allocate
                      </button>
                    )}
                  </div>
                  <ul>
                    {group.boards.map((board) => {
                      const justPinned = pinnedBoardId === board.id
                      const busy = createPin.isPending
                      return (
                        <li key={board.id}>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handlePick(board.id)}
                            className={cn(
                              'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm',
                              'hover:bg-slate-50 disabled:opacity-60',
                              justPinned && 'bg-emerald-50',
                            )}
                          >
                            <span className="min-w-0">
                              <span className="block truncate font-medium text-slate-800">
                                {board.name}
                              </span>
                              {board.parent_board && (
                                <span className="block truncate text-[11px] text-slate-400">
                                  sub-board
                                </span>
                              )}
                            </span>
                            {justPinned ? (
                              <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                            ) : busy ? (
                              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-300" />
                            ) : null}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })
          )}
        </div>

        {createPin.isError && (
          <div className="border-t border-rose-100 bg-rose-50 px-4 py-2 text-xs text-rose-600">
            Could not add to that board. You must be a member of both
            organisations. Please try another board.
          </div>
        )}
      </div>
    </div>
  )
}
